import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { AppContext } from "Context/AppContext";
import HierachicalDropdown from "Components/Common/HierachicalDropdown";
import useSearchBar from "./Components/useSearchBar";
import "styles/main.css";
import SearchBarCreateButton from "./Components/SearchBarCreateButton";
import Search from "Components/DesignSystem/Search/Search";
import Avatar from "Components/DesignSystem/Avatar/Avatar";
import Menu from "Components/DesignSystem/Menu/Menu";
import { useDismissable } from "Components/DesignSystem/useDismissable";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import { HelpCircle } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";
import Typography from "Components/DesignSystem/Typography/Typography";
import Divider from "Components/DesignSystem/Divider/Divider";
import DownloadIndicator from "Components/GMS/Rules/Components/DownloadIndicator";
import AppSettings from "Settings/AppSettings";
import AuthManager from "Common/AuthManager";
import UnsavedChangesModal from "Components/Common/UnsavedChangesModal";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { LogoutIcon } from "assests/GmsIcons";
import { ExternalLink } from "Components/DesignSystem/Iconography/Icons";
import { THEME_LOCKED_TO } from "Components/DesignSystem/theme";
import type { ThemePreference } from "Components/DesignSystem/theme";
import { isCompatioAdmin, currentRoleOf } from "../adminGate";
import BrandLogo from "./Components/BrandLogo";
import SegmentedControl from "Components/DesignSystem/SegmentedControl/SegmentedControl";

const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

/**
 * The signed-in user's mark and menu.
 *
 * **What moved (§ 16).** This was `react-avatar` — a dependency used in exactly one
 * file — plus a hand-rolled popover: a `mousedown` listener for outside-click, no
 * Escape, no arrow keys, no roving focus. It is now DS `Avatar` inside DS `Menu`,
 * which brings all four for free.
 *
 * Three things that were quietly broken and are fixed by the move:
 *
 * - **`hover:tw-bg-grey-50` on Logout was a dead class.** The `grey` family runs
 *   100/200/450/500/550/600/700/900 — there is no `50`, so the row had no hover
 *   background at all. Unresolvable classes are not an error in JSX, in `tsc`, or in
 *   Tailwind's JIT scan; only a test that checks a class against the stylesheet finds
 *   them.
 * - **`hover:tw-text-red-900` resolved to Tailwind's stock `#7f1d1d`**, not the design
 *   system's `tone-danger-ink` (`#ad2a21`). Logout now takes `Menu`'s ordinary
 *   `surface-sunken` hover, which is a real state change where the old one produced
 *   none, and is the same hover every other menu row in the product has.
 * - **`tw-shadow-lg`** was stock Tailwind's black shadow, against a design system whose
 *   elevation is tinted with `grey-900` so it does not go muddy over these neutrals.
 *
 * **Dismissal is owned HERE, not by `Menu`.** `Menu`'s own `onDismiss` scopes
 * "outside" to the menu box alone, so a click on the trigger while open would fire
 * dismiss on `mousedown` and then the trigger's `onClick` would toggle it back open —
 * the menu would refuse to close from its own button. `useDismissable` is therefore
 * wired to the container that holds *both*, the same arrangement `DropdownButton`
 * uses. Non-modal, so it does not lock page scroll or trap Tab.
 */
/*
  Theme is open to everyone now; the admin role check moved to the Design system entry
  and lives in `../adminGate`. Derive it from there rather than comparing the role string
  here — one derivation is what stopped the theme gate's two halves drifting apart, and
  the next admin-only surface will want the same.
*/

/*
  "System", not "System theme": the group is already labelled Theme, so the longer form
  stutters inside it. Bare System is what GitHub, Linear and (as "Auto") macOS settle on,
  and it reads as a choice rather than a category. Three short labels is also exactly what
  SegmentedControl is for — 2-4 mutually-exclusive views; more, or longer, wants a Dropdown.
*/
const THEME_OPTIONS: ReadonlyArray<{ id: ThemePreference; label: string }> = [
  { id: 'system', label: 'System' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

type ProfileDropdownProps = {
  getUserName: () => string;
  onLogout: () => void;
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (preference: ThemePreference) => void;
};

const ProfileDropdown = ({ getUserName, onLogout, themePreference, onThemePreferenceChange }: ProfileDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const userObj = AuthManager.getAuthUser();
  const firstName = userObj?.profile?.first_name ?? 'Guest';
  const lastName = userObj?.profile?.last_name ?? '';
  const fullName = `${capitalize(firstName)} ${capitalize(lastName)}`.trim();
  const email = userObj?.profile?.email ?? '';
  const role = currentRoleOf(userObj);
  /*
    Theme is no longer role-gated — every signed-in user can switch it. What remains is
    a renderability check, not a policy one: `Layout` always supplies the handler, but
    the props are optional so this component stays mountable on its own.
  */
  const canSwitchTheme = typeof onThemePreferenceChange === 'function';
  /*
    The Design system page is an internal reference for the people building the system,
    not a feature of the product — so it takes the admin check the theme used to carry.
    Hiding the entry is the whole of the gate here: the route itself is still reachable
    by URL, which is a separate decision (see `okf/templates/e2e.md`).
  */
  const canSeeDesignSystem = isCompatioAdmin(userObj);

  useDismissable({
    isOpen,
    onDismiss: () => setIsOpen(false),
    isInside: (target) => !!containerRef.current?.contains(target),
  });

  /*
    The design-system showcase is a reference page, not part of a task, so it opens in
    its own tab rather than taking over the one the user is working in.

    Same shape as `onClickHelpIcon` below — `noopener noreferrer` in the features
    string AND `opener = null` after, because the feature string is advisory in some
    older engines while the property assignment is not. A menu item cannot be a real
    `<a href target="_blank">` (DS `MenuItem` renders a `<button>`), so middle-click
    and Cmd-click do not work here; `tooltip` carries the "opens in a new tab" note
    as `aria-describedby` instead, which is the part that actually matters.
  */
  const openDesignSystem = () => {
    const newWindow = window.open('/design-system', '_blank', 'noopener noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  /* Outside the `role="menu"` element: ARIA allows only items, groups and separators
     in a menu, so the identity block goes in `Menu`'s `header` slot. `Menu` draws the
     rule beneath it; the one between identity and role is this block's own. */
  const identity = (
    <>
      <div className="tw-flex tw-items-center tw-gap-3 tw-p-3">
        <Avatar name={getUserName()} size="md" />
        <div className="tw-flex tw-flex-col tw-min-w-0">
          <Typography variant="title-header" as="span" className="tw-truncate tw-text-ink">{fullName}</Typography>
          {email && <Typography variant="caption-metadata" as="span" className="tw-truncate tw-text-ink-tertiary">{email}</Typography>}
        </div>
      </div>
      {role && (
        <>
          <Divider />
          <div className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-2">
            <Typography variant="caption-metadata" as="span">Role</Typography>
            <Typography variant="title" as="span">{role}</Typography>
          </div>
        </>
      )}
      {canSwitchTheme && (
        <>
          <Divider />
          {/*
            The theme picker sits ABOVE the actions, not between them, and that is an
            ARIA constraint rather than a layout preference. `SegmentedControl` is a
            `role="radiogroup"`, and a `role="menu"` may contain only items, groups and
            separators — so a non-menu widget can go before all the items or after all
            of them, never in the middle. Logout belongs last, so this goes first.

            It replaces three `menuitemradio` rows: same semantics (one tab stop, arrow
            keys move AND select), one row instead of three, and the current value reads
            at a glance rather than needing a check mark found down a list.

            `header` carries no padding of its own by contract, hence `px-3 pb-3`.
          */}
          <div className="tw-flex tw-flex-col tw-gap-1.5 tw-px-3 tw-pt-2 tw-pb-3" data-testid="theme-section">
            <Typography variant="sub-title-label" as="span" className="tw-text-ink-tertiary">
              Theme
            </Typography>
            <SegmentedControl
              data-testid="theme-control"
              ariaLabel="Theme"
              value={themePreference ?? 'system'}
              options={THEME_OPTIONS.map((option) => ({
                id: option.id,
                label: option.label,
                /* 2026-09-25 — the theme is locked (`THEME_LOCKED_TO` in theme.ts): only
                   the locked theme stays enabled, so the others read as unavailable
                   rather than as a click that silently does nothing. */
                disabled: THEME_LOCKED_TO !== null && option.id !== THEME_LOCKED_TO,
                'data-testid': `theme-${option.id}-btn`,
              }))}
              /* No `setIsOpen(false)`: picking a theme is a setting, not a destination —
                 the point is seeing the change and being able to try the next option
                 without reopening the menu. */
              onChange={(id) => onThemePreferenceChange?.(id as ThemePreference)}
            />
          </div>
        </>
      )}
    </>
  );

  return (
    <div id="user-avatar-container" className="tw-relative" ref={containerRef}>
      <button
        id="user-avatar-button"
        data-testid="user-avatar-btn"
        aria-label="User menu"
        type="button"
        /* 36x36, so the trigger sits on the same rung as Search, Create and Help and
           the row stops being 40px tall because of its least important element. The
           mark inside is 32 — see `AvatarSize` on why an avatar is one rung below the
           control ladder. `FOCUS_RING` because there was none. */
        className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer ${FOCUS_RING}`}
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Avatar name={getUserName()} size="md" />
      </button>

      {isOpen && (
        <div
          id="user-dropdown-menu"
          className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-[260px] tw-z-50"
        >
          <Menu
            data-testid="user-menu"
            className="tw-shadow-ds-md"
            header={identity}
            /*
              `sections`, not a flat `items`: the theme options are a PICK-ONE group and
              need a heading, and `Menu` draws the rules between sections itself. Giving
              each option `selected` makes it a `menuitemradio` carrying `aria-checked`
              (§ 65) — which is the difference between "three actions" and "one setting
              with three values" to anything reading the menu aloud.
            */
            sections={[
              /*
                Spread, not a `&&` inside the array: a falsy entry would still be an
                ELEMENT of `sections`, and `Menu` draws a rule between sections — so a
                non-admin would get a divider with nothing under it.
              */
              ...(canSeeDesignSystem ? [{
                items: [{
                  id: 'design-system',
                  label: 'Design system',
                  /* DS `ExternalLink`, not `GmsIcons`' `ExternalLinkIcon`: that one is
                     12x12 with `stroke="#0747A6"` baked in, so beside `LogoutIcon` it
                     renders blue and four pixels small. See `Iconography/Icons.tsx`. */
                  icon: <ExternalLink />,
                  tooltip: 'Opens in a new tab',
                  'data-testid': 'design-system-btn',
                }],
              }] : []),
              {
                items: [{
                  id: 'logout',
                  label: 'Logout',
                  icon: <LogoutIcon />,
                  'data-testid': 'logout-btn',
                }],
              },
            ]}
            onItemClick={(itemId) => {
              if (itemId === 'design-system') {
                openDesignSystem();
                setIsOpen(false);
                return;
              }
              if (itemId !== 'logout') return;
              /*
                Order preserved from the pre-migration handler, for fidelity rather than
                for effect: `onLogout` routes through `guardNavigation` synchronously, so
                React batches this `setIsOpen` with whatever the guard sets and the two
                land in the same commit. Swapping them changes nothing a user or a test
                can observe — verified by swapping them and watching all 44 tests stay
                green. Do not write a test that claims otherwise.
              */
              setIsOpen(false);
              onLogout();
            }}
          />
        </div>
      )}
    </div>
  );
};

type SearchbarProps = {
  /* Forwarded straight to the profile menu, where the Theme control lives. `Layout`
     owns the state — see the note there on why the shell rather than this component. */
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (preference: ThemePreference) => void;
};

const Searchbar = ({ themePreference, onThemePreferenceChange }: SearchbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const appData = useContext<any>(AppContext);
  const { editInProgress, editSaveHandler, editCancelHandler } = appData;

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Bug 12460: keep the global search box in sync with the route. On the
  // search results page (/gms/search/:query) show the searched term so it
  // persists (and reappears when navigating back); on any other page clear it.
  useEffect(() => {
    const SEARCH_PREFIX = "/gms/search/";
    if (location.pathname.startsWith(SEARCH_PREFIX)) {
      const rawQuery = location.pathname.slice(SEARCH_PREFIX.length);
      try {
        setSearchValue(decodeURIComponent(rawQuery));
      } catch {
        setSearchValue(rawQuery);
      }
    } else {
      setSearchValue("");
    }
  }, [location.pathname]);

  const hasHandlers = editSaveHandler && editCancelHandler;

  const {
    partyList,
    selectedParty,
    handlePartyChange,
    industryList,
    selectedIndustry,
    handleIndustryChange,
    getUserName,
    channelList,
    selectedChannel,
    handleChannelChange,
    parentChannelList,
    selectedParentChannel,
    handleParentChannelChange,
    environmentList,
    selectedEnvironment,
    handleEnvironmentChange,
    isLoadingParty,
    isLoadingIndustry,
    isLoadingChannel,
    isLoadingEnvironment,
    refreshEnvironments
  } = useSearchBar();


  React.useEffect(() => {
    if (selectedParentChannel &&
        (!environmentList || environmentList.length === 0) &&
        !isLoadingEnvironment &&
        selectedParty?.id) {
      refreshEnvironments(selectedParentChannel.id, selectedParty.id);
    }
  }, [selectedParentChannel, selectedParty]);

  /**
   * Guard navigation actions during edit mode.
   * If handlers are registered, show modal. Otherwise fall back to toast.
   * Returns true if the action was intercepted (caller should abort).
   */
  const guardNavigation = (action: () => void): boolean => {
    if (!editInProgress) return false;
    if (hasHandlers) {
      setPendingAction(() => action);
      setShowUnsavedModal(true);
    } else {
      toast.warning('Please save or cancel your edits before navigating');
    }
    return true;
  };

  const handleModalSave = async () => {
    if (!editSaveHandler) return;
    setSaveInProgress(true);
    try {
      const success = await editSaveHandler();
      if (success) {
        setShowUnsavedModal(false);
        pendingAction?.();
        setPendingAction(null);
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleModalDiscard = () => {
    editCancelHandler?.();
    setShowUnsavedModal(false);
    pendingAction?.();
    setPendingAction(null);
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setPendingAction(null);
  };

  const handleKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter") {
      const target = event.target as HTMLInputElement;
      const searchPath = "/gms/search/" + encodeURIComponent(target.value);
      if (guardNavigation(() => navigate(searchPath))) return;
      navigate(searchPath);
    }
  };

  const handleLogout = () => {
    if (guardNavigation(() => navigate({
  pathname: `/logout`
}, {
  replace: true
}))) return;
    navigate({
  pathname: `/logout`
}, {
  replace: true
});
  };

  const onClickHelpIcon = () => {
    const SAMS_SSO_URL = AppSettings.SAMS_SSO_URL?.split('/landing')[0];
    const SAMS_TOKEN = AuthManager.getSamsToken();
    const url = `${SAMS_SSO_URL}/launchHelpDesk?token=${SAMS_TOKEN}`;
    const newWindow = window.open(url, '_blank', "noopener noreferrer");
    if (newWindow) newWindow.opener = null
  }

  const modalRoot = document.querySelector('#modal-root');

  return (
    <React.Fragment>
      {window.location.pathname && (
       <nav
        id="main-navigation"
        data-testid="main-navigation"
        /* `surface`, was `tw-bg-white` — the one hardcoded colour in the whole top bar, and
           the reason the header stayed a white band over a dark sidebar (§ 21). */
        className={`tw-max-w-screen tw-bg-surface tw-sticky tw-top-0 tw-left-0 tw-flex tw-items-center tw-flex-wrap md:tw-justify-start md:tw-flex-nowrap tw-m-0`}>
        <div
          id="navigation-container"
          /* A bare `tw-border` takes `borderColor.DEFAULT` — `#DFE0E6`, a light grey with
             no dark value. And `tw-shadow-md` is stock Tailwind's BLACK shadow, invisible
             on a dark ground and muddy over these blue-grey neutrals in light; `ds-sm` is
             tinted with grey-900 for exactly that reason. */
          className={`tw-flex tw-flex-nowrap tw-items-center tw-justify-between tw-py-[2px] tw-px-3 tw-pointer-events-auto tw-w-full tw-border tw-border-edge tw-shadow-ds-sm`}
        >
        <div id="navigation-left-section" className={`tw-flex tw-items-center tw-space-x-8`}>
          <BrandLogo className="tw-relative tw-m-1 tw-ml-0 tw-h-6" />
          <div id="navigation-dropdown-container" className={`tw-h-full`}>
            <div id="navigation-dropdown-wrapper" className={`tw-flex tw-items-center tw-space-x-1.5`}>
              <HierachicalDropdown
                id="workspace-domain-dropdown"
                columnOptions={[
                  {
                    title: "Workspace",
                    name: "workspace",
                    nameColumn: "name",
                    idColumn: "id"
                  },
                  {
                    title: "Domain",
                    name: "domain",
                    nameColumn: "name",
                    idColumn: "id"
                  },
                  {
                    title: "Channel",
                    name: "channel",
                    nameColumn: "name",
                    idColumn: "id"
                  },
                  {
                    title: "Environment",
                    name: "environment",
                    nameColumn: "name",
                    idColumn: "id"
                  },
                ]}
                data={[
                  partyList || [],
                  industryList || [],
                  parentChannelList || [],
                  environmentList || []
                ]}
                selected={[
                  selectedParty,
                  selectedIndustry,
                  selectedParentChannel,
                  selectedEnvironment
                ]}
                onClick={[
                  handlePartyChange,
                  handleIndustryChange,
                  handleParentChannelChange,
                  handleEnvironmentChange
                ]}
                isLoading={{
                  0: isLoadingParty,
                  1: isLoadingIndustry,
                  2: isLoadingChannel,
                  3: isLoadingEnvironment
                }}
                editInProgress={editInProgress}
                editSaveHandler={editSaveHandler}
                editCancelHandler={editCancelHandler}
              />
            </div>
          </div>
        </div>

        {/*
          `items-center` is load-bearing. Without it this row is `align-items: stretch`,
          so `#search-container` — a plain div — stretched to the row's height (set by the
          40px avatar) while DS `Search`'s field stayed a declared 36px pinned to the TOP
          of that stretched box. `#navigation-actions-container` below carries its own
          `items-center`, so Create, Help and the avatar were centred and the search alone
          sat ~4px high. Every control here now shares one centre line.
        */}
        <div id="navigation-right-section" className={`tw-flex tw-items-center tw-space-x-3`}>
          {appData.downloadState.downloadFlag ?
            <div id="download-indicator-container" className="tw-flex tw-items-center">
              <DownloadIndicator
                id="download-indicator"
                message={appData?.downloadState?.message ?? ''}/>
            </div> : null}
          <div id="search-container">
            {/*
              The global search, on DS `Search` (§ 12). Four things about this call site
              are deliberate:

              - **`onKeyDown`, not `onSearch`.** `Search` fires `onSearch('')` from its
                clear button *and* `onSearch(value)` from Enter, and the two are
                indistinguishable at the callback. Since Enter here NAVIGATES, wiring
                `onSearch` would make pressing the ✕ navigate to `/gms/search/` — the
                route matches (`:query?` is optional), so it would silently land the user
                on an empty results page instead of clearing the box. Routing Enter
                through `onKeyDown` leaves clearing a pure text operation and keeps Enter
                byte-identical to the pre-migration handler.
              - **No `aria-describedby`.** The old input pointed at `#basic-addon1`, an
                element that exists nowhere in this repo — leftover Bootstrap boilerplate.
                `Search` composes its own describedby from its helper text.
              - **A stated width.** The row was `w-min`, i.e. whatever an `<input>`'s
                intrinsic 20-character box happens to measure in the current font. A
                declared width is about the same size and does not move when the type does.
              - **No `id` on the input.** `Search` owns it (`useId`, for the label
                association); an `id` passed in is overwritten. Nothing outside this file
                referenced `#search-input` — checked — so the hook is the testid.
            */}
            <Search
              data-testid="global-search"
              className="tw-w-[220px]"
              placeholder="Search"
              value={searchValue}
              onChange={(next) => setSearchValue(next)}
              onKeyDown={handleKeyPress}
            />
          </div>

          <div id="navigation-actions-container" className={`tw-flex tw-items-center tw-h-full tw-space-x-3`}>
            <SearchBarCreateButton id="create-button" guardNavigation={guardNavigation}/>
            {/*
              Help. The glyph is now the design system's `HelpCircle` rather than
              FontAwesome's `far fa-question-circle` — a second icon family, filled where
              ours is line-drawn, pulled from a CDN on every page load.

              **TODO — this is a deviation, and the reason is a real gap.** The design
              system has no `IconButton`. `Button` always applies `BUTTON_INSET` (px-4 at
              `md`), which turns a single 20px glyph into a 36×52 pill, and `className`
              cannot walk that back: `tw-px-0` and `tw-px-4` are different utilities, so
              which one wins is decided by their order in the compiled stylesheet, not by
              the order they are written here. So this follows the pattern the design
              system uses for its OWN icon-only controls — `Dialog`'s close, `Search`'s
              clear, `Toast`'s dismiss are all a bare `<button>` with padding, a radius,
              ink tokens and `FOCUS_RING`. Fourth repetition of it; it wants extracting.

              What it gains over the markup it replaces: a visible focus ring (there was
              none — this was reachable by keyboard with nothing to show for it), a hover
              state, ink tokens instead of `tw-text-gray-500`, and a 36×36 box that clears
              WCAG 2.2 SC 2.5.8's 24×24 minimum with room to spare.
            */}
            <Tooltip content="Open the help desk" placement="bottom">
              <button data-testid="help-btn"
                aria-label="Help"
                id="help-button"
                type="button"
                onClick={(e) => { e.stopPropagation(); onClickHelpIcon(); }}
                className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-ctl tw-text-ink-tertiary hover:tw-bg-surface-hover-strong hover:tw-text-ink tw-transition-colors ${FOCUS_RING}`}>
                <HelpCircle size={20} aria-hidden="true" />
              </button>
            </Tooltip>

            <ProfileDropdown
              getUserName={getUserName}
              onLogout={handleLogout}
              themePreference={themePreference}
              onThemePreferenceChange={onThemePreferenceChange}
            />
          </div>
        </div>
      </div>
      </nav>
      )}
      {modalRoot ? <iframe
        style={{ display: "none" }}
        src={appData?.downloadUrl}
        onLoad={() => console.log("onLoad")}
        id="iframe_download_cbm"
        title="Download CBM"
      /> : null}
      {showUnsavedModal && (
        <UnsavedChangesModal
          id="searchbar-unsaved-changes-modal"
          title="Unsaved Changes"
          message="You have unsaved changes. Do you want to save them before navigating?"
          confirmText="Save Changes"
          cancelText="Cancel"
          discardText="Discard changes"
          onClose={handleModalCancel}
          onConfirm={handleModalSave}
          onDiscard={handleModalDiscard}
          saveInProgress={saveInProgress}
        />
      )}
    </React.Fragment>
  );
};

export default Searchbar;
